import {
  keepPreviousData,
  queryOptions,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query'

import { hasCode } from '@/api/errors'
import { sectorsKeys } from '@/features/sectors/api/keys'
import { sectorsService } from '@/features/sectors/api/sectorsService'
import type { SectorStats, SectorsOverview, SectorView } from '@/schemas/sectors'

/**
 * The whole Market · Sectors tab in one query — the route's MAIN data, keyed
 * on the `view` chip. `/market/sectors`'s loader awaits it for the ENTRY
 * URL's view (no `loaderDeps`: a chip click re-keys this query from the
 * screen, not from a re-run loader — see the loader's own comment).
 *
 * The strip, the donut and the seven cards are three readings of one session's
 * turnover, so they arrive together — split across three queries, the page
 * could draw a donut from one refresh above cards from the next and print two
 * different totals for the same day.
 *
 * The factory carries the key and the fetch only; `useSectors` adds the
 * `keepPreviousData` a chip click needs.
 */
export const sectorsOverviewQueryOptions = (view: SectorView) =>
  queryOptions<SectorsOverview>({
    queryKey: sectorsKeys.overview(view),
    queryFn: () => sectorsService.overview(view),
  })

/**
 * No polling, unlike Overview and Movers: this tab is a summary of the close,
 * and its own service builds it from a fixed set of records.
 */
export const useSectors = (view: SectorView): UseQueryResult<SectorsOverview> =>
  useQuery<SectorsOverview>({
    ...sectorsOverviewQueryOptions(view),
    // The view chips are the whole page's key, so without this a switch empties
    // the strip, the donut and all seven cards at once. That is not just a
    // flash: the page briefly becomes shorter than the viewport, and the
    // browser clamps the member's scroll position to the top on the way past.
    // Under the route's loader the FIRST view is always cached before mount,
    // so `data` is defined from the first render on and stays defined for the
    // rest of the screen's life — the page-level loading/failed branches
    // SectorsScreen used to carry are gone; see its own comment.
    placeholderData: keepPreviousData,
  })

/**
 * Half an hour. The medians aggregate each company's newest fundamentals row
 * (quarterly) and daily valuation row, so nothing a member could notice moves
 * inside a session. On the factory because `prefetchRouteData` honours the
 * query's OWN staleTime.
 */
const SECTOR_STATS_STALE_MS = 30 * 60_000

/**
 * One sector's medians — the Stock page's "vs sector median" and the Peers
 * table's Sector column. `null` is `404 SECTOR_NOT_FOUND` as a value
 * (docs/route-loading.md, "Not-found answers"): a sector symbol mm-be does
 * not know costs the comparison, not the card.
 */
export const sectorStatsQueryOptions = (symbol: string) =>
  queryOptions<SectorStats | null>({
    queryKey: sectorsKeys.stats(symbol),
    queryFn: async () => {
      try {
        return await sectorsService.stats(symbol)
      } catch (error) {
        if (hasCode(error, 'SECTOR_NOT_FOUND')) return null
        throw error
      }
    },
    staleTime: SECTOR_STATS_STALE_MS,
  })

/**
 * Silent until there is a sector to ask about: `CompanyDetail.profile.sectorSymbol`
 * is null for a company mm-be files under no sector, and an empty path segment
 * is not a sector.
 */
export const useSectorStats = (symbol: string | null): UseQueryResult<SectorStats | null> =>
  useQuery({ ...sectorStatsQueryOptions(symbol ?? ''), enabled: symbol !== null && symbol !== '' })
